import React from 'react';
import { Spin } from 'antd';
import { Container } from '@/components/ui/Container';
import { useSalesLeadDetail } from '@hooks/sales/useSalesLeads';
import StatusBadge from '@/components/ui/StatusBadge';
import { DetailLabel, DetailValue, DetailPanelLoading, DetailValueInherit } from './style';

const capFirst = (s) => (s && s.length ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '—');
const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
        return new Date(dateStr).toLocaleString();
    } catch {
        return dateStr;
    }
};

/**
 * Content for the sales lead side panel. Use inside <SidePanel>.
 * Uses useSalesLeadDetail from useSalesLeads hook file.
 */
const SalesLeadDetailPanel = ({ leadId }) => {
    const { data: lead, isLoading, isError } = useSalesLeadDetail(leadId);

    if (!leadId) return null;
    if (isLoading) {
        return (
            <DetailPanelLoading>
                <Spin size="large" />
            </DetailPanelLoading>
        );
    }
    if (isError || !lead) {
        return (
            <Container $marginBottom="24px">
                <DetailValueInherit>Failed to load lead details.</DetailValueInherit>
            </Container>
        );
    }

    return (
        <>
            <Container $marginBottom="24px" $display="flex" $alignItems="flex-start" $gap="12px">
                <DetailLabel>Email :</DetailLabel>
                <DetailValue>{lead.email ?? '—'}</DetailValue>
            </Container>
            <Container $marginBottom="24px" $display="flex" $alignItems="flex-start" $gap="12px">
                <DetailLabel>Phone :</DetailLabel>
                <DetailValue>{lead.phone ?? '—'}</DetailValue>
            </Container>
            <Container $marginBottom="24px" $display="flex" $alignItems="flex-start" $gap="12px">
                <DetailLabel>Company :</DetailLabel>
                <DetailValue>{lead.company ?? '—'}</DetailValue>
            </Container>
            <Container $marginBottom="24px" $display="flex" $alignItems="flex-start" $gap="12px">
                <DetailLabel>Source :</DetailLabel>
                <DetailValue>{capFirst((lead.source || '').replace(/_/g, ' ')) || '—'}</DetailValue>
            </Container>
            <Container $marginBottom="24px" $display="flex" $alignItems="flex-start" $gap="12px">
                <DetailLabel>Stage :</DetailLabel>
                <DetailValue>{capFirst((lead.stage || '').replace(/_/g, ' ')) || '—'}</DetailValue>
            </Container>
            <Container $marginBottom="24px" $display="flex" $alignItems="flex-start" $gap="12px">
                <DetailLabel>Status :</DetailLabel>
                <DetailValue>
                    <StatusBadge status={lead.status} />
                </DetailValue>
            </Container>
            {lead.assignedToName != null && (
                <Container $marginBottom="24px" $display="flex" $alignItems="flex-start" $gap="12px">
                    <DetailLabel>Assigned to :</DetailLabel>
                    <DetailValue>{lead.assignedToName ?? lead.assignedToUserId ?? '—'}</DetailValue>
                </Container>
            )}
            {lead.notes && (
                <Container $marginBottom="24px" $display="flex" $flexDirection="column" $gap="8px">
                    <DetailLabel>Notes :</DetailLabel>
                    <DetailValue>{lead.notes}</DetailValue>
                </Container>
            )}
            <Container $marginBottom="24px" $display="flex" $alignItems="flex-start" $gap="12px">
                <DetailLabel>Created at :</DetailLabel>
                <DetailValue>{formatDate(lead.createdAt)}</DetailValue>
            </Container>
            {lead.updatedAt != null && (
                <Container $marginBottom="24px" $display="flex" $alignItems="flex-start" $gap="12px">
                    <DetailLabel>Updated at :</DetailLabel>
                    <DetailValue>{formatDate(lead.updatedAt)}</DetailValue>
                </Container>
            )}
        </>
    );
};

export default SalesLeadDetailPanel;
